import { useState } from 'react';
import { isEmbeddedMode } from '@/utils/embeddedMode';
import {
  isPhotoPickerAvailable,
  base64ToFile,
  setupMultiMediaCallback,
  triggerNativeMediaPicker,
} from '@/utils/photoPicker';
import { usePlan } from './usePlan';

interface UploadImageMutation {
  mutateAsync: (args: {
    vehicleId: string;
    file: File;
    documentType?: string;
    onUploadProgress?: (progress: number) => void;
  }) => Promise<unknown>;
}

interface HandleTakePhotoOptions {
  documentType?: string | null;
  onComplete?: () => void;
}

interface UseVehicleMediaUploadOptions {
  vehicleId: string | undefined;
  uploadImage: UploadImageMutation;
  onError?: (message: string) => void;
}

export function useVehicleMediaUpload({
  vehicleId,
  uploadImage,
  onError,
}: UseVehicleMediaUploadOptions) {
  // Video is a plan feature (Pro and up); the backend refuses a video on a
  // plan without it, so the picker offers only photos there.
  const { includes } = usePlan();
  const canUploadVideo = includes('video');
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined
  );
  const [uploadingFilesCount, setUploadingFilesCount] = useState(1);
  const isEmbedded = isEmbeddedMode();

  const uploadFile = async (file: File, documentType?: string | null) => {
    if (!vehicleId) return;
    setUploadProgress(0);
    try {
      await uploadImage.mutateAsync({
        vehicleId,
        file,
        documentType: documentType || undefined,
        onUploadProgress: setUploadProgress,
      });
    } catch (error) {
      // A refusal, such as a video on a plan without video, says why.
      const refused = (error as { status?: number }).status === 403;
      onError?.(
        refused && error instanceof Error
          ? error.message
          : 'Failed to upload media. Please try again.'
      );
    } finally {
      setUploadProgress(undefined);
    }
  };

  const handleTakePhoto = (options?: HandleTakePhotoOptions) => {
    if (!vehicleId) return;

    const { documentType, onComplete } = options ?? {};

    if (isEmbedded && isPhotoPickerAvailable()) {
      const cleanup = setupMultiMediaCallback(
        async (base64Data: string, mediaType: string) => {
          try {
            const file = await base64ToFile(
              base64Data,
              `vehicle_${mediaType}_${Date.now()}.${mediaType === 'video' ? 'mp4' : 'jpg'}`,
              mediaType
            );
            await uploadFile(file, documentType);
          } catch (error) {
            // error handled in uploadFile
          }
        },
        () => {
          cleanup();
          onComplete?.();
        }
      );
      triggerNativeMediaPicker(canUploadVideo);
    } else {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = canUploadVideo ? 'image/*,video/*' : 'image/*';
      input.capture = 'environment';
      input.multiple = true;

      input.onchange = async (event) => {
        const files = (event.target as HTMLInputElement)?.files;
        if (!files) return;

        const fileArray = Array.from(files);
        setUploadingFilesCount(fileArray.length);
        for (const file of fileArray) {
          await uploadFile(file, documentType);
        }

        onComplete?.();
      };

      input.click();
    }
  };

  return { uploadProgress, uploadingFilesCount, handleTakePhoto };
}
